import type { JSONContent } from '@tiptap/core';
import { v4 as uuid } from 'uuid';
import { ContentBlockType, TableBlock, TableCell } from '../../model';
import { BlockToTiptapConverter, convertBlocksToTiptapNodes, ConverterContext } from '../blocks-to-tiptap.converter';
import { convertTiptapNodesToBlocks } from '../tiptap-to-blocks.converter';

/**
 * Converts a TableBlock to a Tiptap table node.
 * Recursively converts nested content blocks in each cell.
 *
 * @param block - The TableBlock to convert
 * @param context - Optional context to pass through (e.g., 'popover' to skip popover processing)
 * @returns A Tiptap JSON node representing the table
 */
export const convertTableBlockToTiptap = ((block, context?: ConverterContext) => {
  return {
    type: 'table',
    attrs: {
      borderVisible: block.has_borders,
      ...(block.custom_css && { custom_css: block.custom_css }),
      ...(block.custom_style && { custom_style: block.custom_style }),
    },
    content: block.rows.map((row) => ({
      type: 'tableRow',
      content: row.cells.map((cell) => convertCellToTiptap(cell, context)),
    })),
  };
}) satisfies BlockToTiptapConverter<TableBlock>;

/**
 * Converts a TableCell to a Tiptap tableCell or tableHeader node.
 */
function convertCellToTiptap(cell: TableCell, context?: ConverterContext): JSONContent {
  return {
    type: cell.is_header ? 'tableHeader' : 'tableCell',
    attrs: {
      colspan: cell.colspan ?? 1,
      rowspan: cell.rowspan ?? 1,
      ...(cell.background_color && { backgroundColor: cell.background_color }),
      ...(cell.text_align && { nodeTextAlign: cell.text_align }),
      ...(cell.vertical_align && { nodeVerticalAlign: cell.vertical_align }),
      ...(cell.colwidth && { colwidth: cell.colwidth }),
    },
    content: convertBlocksToTiptapNodes(cell.content, context),
  };
}

/**
 * Converts a Tiptap table node back to a TableBlock.
 * Recursively converts nested content.
 *
 * @param json - The Tiptap JSON node to convert
 * @returns A TableBlock
 */
export function convertTiptapToTableBlock(json: JSONContent): TableBlock {
  const tableRows = (json.content ?? []).filter((node) => node.type === 'tableRow');

  return {
    id: uuid(),
    type: ContentBlockType.Table,
    has_borders: json.attrs?.['borderVisible'] ?? true,
    ...(json.attrs?.['custom_css'] && { custom_css: json.attrs['custom_css'] as string }),
    ...(json.attrs?.['custom_style'] && { custom_style: json.attrs['custom_style'] as Record<string, string> }),
    rows: tableRows.map((row) => {
      const tiptapCells = (row.content ?? []).filter(
        (node) => node.type === 'tableCell' || node.type === 'tableHeader',
      );

      return {
        cells: tiptapCells.map((cell) => convertTiptapToCell(cell)),
      };
    }),
  };
}

/**
 * Converts a Tiptap tableCell or tableHeader node to a TableCell.
 */
function convertTiptapToCell(cell: JSONContent): TableCell {
  const attrs = cell.attrs ?? {};
  const isHeader = cell.type === 'tableHeader';

  return {
    content: convertTiptapNodesToBlocks(cell.content ?? []),
    ...(isHeader && { is_header: true }),
    ...(attrs['backgroundColor'] && { background_color: attrs['backgroundColor'] as string }),
    ...(attrs['nodeTextAlign'] && { text_align: attrs['nodeTextAlign'] as 'left' | 'center' | 'right' }),
    ...(attrs['nodeVerticalAlign'] && { vertical_align: attrs['nodeVerticalAlign'] as 'top' | 'middle' | 'bottom' }),
    ...((attrs['colspan'] ?? 1) > 1 && { colspan: attrs['colspan'] as number }),
    ...((attrs['rowspan'] ?? 1) > 1 && { rowspan: attrs['rowspan'] as number }),
    ...(attrs['colwidth'] && { colwidth: attrs['colwidth'] as number[] }),
  };
}
